import React, { useState, type KeyboardEvent } from "react";
import { EyeIcon, EyeOffIcon, Spinner } from "./Shared/Icons";
import type { useLogin } from "../hooks/useLogin";

interface NewPasswordViewProps {
  auth: ReturnType<typeof useLogin>;
  isDark: boolean;
}

export const NewPasswordView: React.FC<NewPasswordViewProps> = ({ auth, isDark }) => {
  const {
    newPassword,
    confirmPassword,
    error,
    isLoading,
    setNewPassword,
    setConfirmPassword,
    handleNewPassword,
  } = auth;

  const [showNewPass, setShowNewPass] = useState(false);
  const [showConfirmPass, setShowConfirmPass] = useState(false);

  const onKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter" && !isLoading) handleNewPassword();
  };

  return (
    <div className="flex flex-col gap-4">
      <div className={`mx-auto w-12 h-12 rounded-full bg-yellow-500/10 border border-yellow-500/30 text-yellow-500 flex items-center justify-center mb-4 text-xl`}>
        🔒
      </div>

      <h1 className="text-2xl font-black text-center tracking-tight mb-2">Establecé tu contraseña</h1>
      <p className={`text-xs text-center leading-relaxed mb-3 ${isDark ? "text-white/40" : "text-gray-500"}`}>
        Por seguridad, necesitás cambiar tu contraseña la primera vez que ingresás.
      </p>

      {error && (
        <div className="flex items-center gap-2 px-4 py-3 bg-rose-500/10 border border-rose-500/20 text-rose-400 rounded-xl text-xs font-semibold mb-4 leading-normal">
          <span>⚠️</span>
          <span>{error}</span>
        </div>
      )}

      {/* New Password */}
      <div className="relative">
        <input
          type={showNewPass ? "text" : "password"}
          value={newPassword}
          onChange={(e) => setNewPassword(e.target.value)}
          onKeyDown={onKeyDown}
          placeholder="Nueva contraseña"
          className={`w-full py-4 px-5 border rounded-xl text-xs font-medium transition-all outline-none ${
            isDark
              ? "bg-white/[0.03] border-white/10 text-white placeholder-white/20 focus:border-[#058b95] focus:bg-[#121214]"
              : "bg-[#F5F7F4] border-transparent text-[#212121] placeholder-gray-400 focus:border-[#058b95] focus:bg-white"
          }`}
          disabled={isLoading}
          autoFocus
        />
        <button
          type="button"
          onClick={() => setShowNewPass(!showNewPass)}
          className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-400 hover:text-[#058b95] transition-all text-xs flex items-center justify-center"
        >
          {showNewPass ? <EyeOffIcon /> : <EyeIcon />}
        </button>
      </div>

      {/* Confirm Password */}
      <div className="relative">
        <input
          type={showConfirmPass ? "text" : "password"}
          value={confirmPassword}
          onChange={(e) => setConfirmPassword(e.target.value)}
          onKeyDown={onKeyDown}
          placeholder="Confirmar contraseña"
          className={`w-full py-4 px-5 border rounded-xl text-xs font-medium transition-all outline-none ${
            isDark
              ? "bg-white/[0.03] border-white/10 text-white placeholder-white/20 focus:border-[#058b95] focus:bg-[#121214]"
              : "bg-[#F5F7F4] border-transparent text-[#212121] placeholder-gray-400 focus:border-[#058b95] focus:bg-white"
          }`}
          disabled={isLoading}
        />
        <button
          type="button"
          onClick={() => setShowConfirmPass(!showConfirmPass)}
          className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-400 hover:text-[#058b95] transition-all text-xs flex items-center justify-center"
        >
          {showConfirmPass ? <EyeOffIcon /> : <EyeIcon />}
        </button>
      </div>

      <div className={`text-[10px] leading-normal px-1 ${isDark ? "text-white/30" : "text-gray-400"}`}>
        Mínimo 8 caracteres, 1 mayúscula, 1 minúscula y 1 número.
      </div>

      <button
        onClick={handleNewPassword}
        disabled={isLoading}
        className="w-full py-4 bg-[#058b95] hover:bg-[#04727a] disabled:opacity-60 text-white font-bold rounded-xl text-sm transition-all shadow-lg shadow-cyan-500/20 active:scale-[0.98] mt-2 flex items-center justify-center gap-2"
      >
        {isLoading ? (
          <>
            <Spinner />
            <span>Guardando...</span>
          </>
        ) : (
          "Confirmar contraseña"
        )}
      </button>
    </div>
  );
};
